import React from "react";
import { Phone, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import { RisingBubbles } from "./Bubbles";
import { SITE } from "../lib/site";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-[112px] pb-20 lg:pt-[140px] lg:pb-32 overflow-hidden"
    >
      {/* Subtle hero gradient lift over the global underwater bg */}
      <div className="absolute inset-0 -z-10">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.15) 60%, rgba(255,255,255,0) 100%)",
          }}
        />
      </div>

      <RisingBubbles count={16} />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        {/* Copy */}
        <div className="lg:col-span-7">
          <div
            className="inline-flex items-center gap-2 px-3 h-8 rounded-full vgc-glass text-xs font-semibold tracking-wide text-[#0F4C81] mb-6"
            data-testid="hero-tag"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Streak-Free Guarantee · Baton Rouge
          </div>

          <h1 className="text-[44px] leading-[1.05] sm:text-5xl lg:text-[68px] lg:leading-[1.02] font-extrabold text-[#0B2542]">
            Crystal-clear windows,{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-[#0F4C81]">spotless results.</span>
              <span
                aria-hidden
                className="absolute inset-x-0 bottom-1 h-3 bg-[#90E0EF]/60 rounded-full -z-0"
              />
            </span>
          </h1>

          <p className="mt-6 text-lg text-slate-600 max-w-2xl">
            Professional residential & commercial window cleaning across Baton Rouge.
            Standard package covers exterior glass, frames, sills, and tracks — interior
            cleaning, screens, and solar panels available as add-ons. 100% satisfaction
            guarantee.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href="#quote"
              data-testid="hero-quote-btn"
              className="group inline-flex items-center justify-center gap-2 h-14 px-7 rounded-full bg-[#0F4C81] text-white font-semibold hover:bg-[#0B3A66] transition-all hover:shadow-[0_18px_40px_rgba(15,76,129,0.25)]"
            >
              Get a Free Quote
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href={`tel:${SITE.phoneTel}`}
              data-testid="hero-call-btn"
              className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-full bg-white text-[#0F4C81] font-semibold border border-blue-100 hover:border-[#00B4D8] transition-colors"
            >
              <Phone className="h-5 w-5" />
              Call {SITE.phoneDisplay}
            </a>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-600">
            <div className="flex items-center gap-2" data-testid="hero-trust-1">
              <ShieldCheck className="h-4 w-4 text-[#00B4D8]" />
              100% Satisfaction Guarantee
            </div>
            <div className="flex items-center gap-2" data-testid="hero-trust-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[#00B4D8]" />
              Residential & Commercial
            </div>
            <div className="flex items-center gap-2" data-testid="hero-trust-3">
              <span className="h-1.5 w-1.5 rounded-full bg-[#00B4D8]" />
              Hours: {SITE.hours}
            </div>
          </div>
        </div>

        {/* Right side: glass info panel (no photo) */}
        <div className="lg:col-span-5 relative">
          <div className="rounded-[28px] vgc-glass ring-1 ring-white/60 vgc-card-shadow p-7 lg:p-8 relative overflow-hidden">
            <div
              aria-hidden
              className="absolute -top-24 -right-24 h-64 w-64 rounded-full"
              style={{
                background:
                  "radial-gradient(circle, rgba(0,180,216,0.45), rgba(0,180,216,0) 65%)",
              }}
            />
            <div className="relative">
              <div className="flex items-center gap-3">
                <span className="h-16 w-16 flex items-center justify-center">
                  <img
                    src={SITE.logoUrl}
                    alt="Vision Glass Care"
                    className="h-16 w-16 object-contain"
                  />
                </span>
                <div>
                  <div className="font-[Outfit] font-bold text-[#0B2542] text-xl leading-tight">
                    Vision Glass Care
                  </div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#0F4C81]/70 font-semibold">
                    Baton Rouge · LA
                  </div>
                </div>
              </div>

              <ul className="mt-7 space-y-4">
                <li className="flex items-start gap-3" data-testid="hero-info-1">
                  <span className="mt-1 h-7 w-7 rounded-full bg-[#00B4D8]/15 text-[#00B4D8] flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="font-[Outfit] font-bold text-[#0B2542]">100% Satisfaction Guarantee</div>
                    <div className="text-sm text-slate-600">If a streak shows up, we come back free.</div>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-testid="hero-info-2">
                  <span className="mt-1 h-7 w-7 rounded-full bg-[#00B4D8]/15 text-[#00B4D8] flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="font-[Outfit] font-bold text-[#0B2542]">Frames, sills &amp; tracks included</div>
                    <div className="text-sm text-slate-600">No upcharge — every package, every time.</div>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-testid="hero-info-3">
                  <span className="mt-1 h-7 w-7 rounded-full bg-[#00B4D8]/15 text-[#00B4D8] flex items-center justify-center flex-shrink-0">
                    <Phone className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="font-[Outfit] font-bold text-[#0B2542]">{SITE.phoneDisplay}</div>
                    <div className="text-sm text-slate-600">{SITE.hours} · text or call anytime</div>
                  </div>
                </li>
              </ul>

              <div className="mt-7 pt-6 border-t border-blue-100 flex items-center justify-between">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">
                  Same-week appointments
                </div>
                <a
                  href="#quote"
                  className="text-sm font-bold text-[#0F4C81] hover:text-[#00B4D8] transition-colors"
                  data-testid="hero-card-cta"
                >
                  Book →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
